/**
 * Sanctum Audio Player
 * Custom audio player controls for Sanctum posts
 */

class SanctumAudioPlayer {
  constructor(container) {
    this.container = container;
    this.audio = container.querySelector('.sanctum-audio__source');
    this.playBtn = container.querySelector('.sanctum-audio__play');
    this.progress = container.querySelector('.sanctum-audio__progress');
    this.progressBar = container.querySelector('.sanctum-audio__progress-bar');
    this.currentTime = container.querySelector('.sanctum-audio__current');
    this.duration = container.querySelector('.sanctum-audio__duration');
    this.volumeBtn = container.querySelector('.sanctum-audio__volume-btn');
    this.volumeSlider = container.querySelector('.sanctum-audio__volume-slider');

    if (this.audio) {
      this.init();
    }
  }

  init() {
    // Play/Pause
    this.playBtn?.addEventListener('click', () => this.togglePlay());

    // Audio events
    this.audio.addEventListener('loadedmetadata', () => this.updateDuration());
    this.audio.addEventListener('timeupdate', () => this.updateProgress());
    this.audio.addEventListener('ended', () => this.handleEnd());

    // If metadata is already loaded (from cache)
    if (this.audio.readyState >= 1) {
      this.updateDuration();
    }

    // Progress click
    this.progress?.addEventListener('click', (e) => this.seek(e));
    this.progress?.addEventListener('touchstart', (e) => this.seek(e), { passive: false });

    // Volume
    this.volumeSlider?.addEventListener('input', (e) => this.setVolume(e));
    this.volumeBtn?.addEventListener('click', () => this.toggleMute());
  }

  togglePlay() {
    if (this.audio.paused) {
      this.audio.play().catch(err => console.error("Audio playback failed:", err));
      this.container.classList.add('is-playing');
    } else {
      this.audio.pause();
      this.container.classList.remove('is-playing');
    }
  }

  updateDuration() {
    if (this.audio.duration && !isNaN(this.audio.duration)) {
      this.duration.textContent = this.formatTime(this.audio.duration);
    }
  }

  updateProgress() {
    const duration = this.audio.duration;
    const currentTime = this.audio.currentTime;
    
    if (duration && !isNaN(duration) && duration !== Infinity) {
      const percent = (currentTime / duration) * 100;
      if (this.progressBar) {
        this.progressBar.style.width = `${percent}%`;
      }
      if (this.currentTime) {
        this.currentTime.textContent = this.formatTime(currentTime);
      }
    }
  }

  seek(e) {
    const duration = this.audio.duration;
    if (!duration || isNaN(duration) || duration === Infinity) {
      return;
    }

    // Handle both click and touch
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const rect = this.progress.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.min(Math.max(x / rect.width, 0), 1);
    
    this.audio.currentTime = percent * duration;
    
    // If paused, update progress bar immediately for visual feedback
    if (this.audio.paused) {
      this.updateProgress();
    }

    if (e.type === 'touchstart') {
      e.preventDefault();
    }
  }

  setVolume(e) {
    this.audio.volume = e.target.value;
    this.updateVolumeIcon();
  }

  toggleMute() {
    this.audio.muted = !this.audio.muted;
    this.updateVolumeIcon();
  }

  updateVolumeIcon() {
    const icon = this.volumeBtn?.querySelector('i');
    if (!icon) return;

    icon.className = 'fas ';
    if (this.audio.muted || this.audio.volume === 0) {
      icon.className += 'fa-volume-mute';
    } else if (this.audio.volume < 0.5) {
      icon.className += 'fa-volume-down';
    } else {
      icon.className += 'fa-volume-up';
    }
  }

  handleEnd() {
    this.container.classList.remove('is-playing');
    this.progressBar.style.width = '0%';
  }

  formatTime(seconds) {
    if (isNaN(seconds) || seconds === Infinity) return '0:00';
    
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}

// Initialize all audio players
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-audio-player]').forEach(el => {
    new SanctumAudioPlayer(el);
  });
});
